'use client'

import { useState, useSyncExternalStore, type ReactNode } from 'react'

import { cn } from '@/lib/utils'
import { Accordion } from '@/components/ui/accordion'

/** Tailwind's `md` breakpoint, as a query a script can ask. */
const BELOW_MD = '(max-width: 767.98px)'

const subscribeToBelowMd = (onChange: () => void) => {
    const query = window.matchMedia(BELOW_MD)
    query.addEventListener('change', onChange)
    return () => query.removeEventListener('change', onChange)
}
const isBelowMdNow = () => window.matchMedia(BELOW_MD).matches
/** Whether the page has hydrated differs only between the server's snapshot and the
 *  client's, and never changes after, so there is nothing to subscribe to. */
const noChangesToReport = () => () => undefined

/**
 * Every item open on the server and in the hydrating render, so the markup hydrates
 * unchanged and carries every answer; below `md` an item then closes until its question is
 * pressed. Until hydration, a stylesheet rule hides the answers on a narrow screen, so
 * nothing opens and shuts on load.
 */
export function CollapsedBelowMdAccordion({
    ids,
    className,
    children,
}: {
    readonly ids: readonly string[]
    readonly className?: string
    readonly children: ReactNode
}) {
    const isBelowMd = useSyncExternalStore(subscribeToBelowMd, isBelowMdNow, () => false)
    const isHydrated = useSyncExternalStore(
        noChangesToReport,
        () => true,
        () => false,
    )
    const [chosen, setChosen] = useState<Readonly<Record<string, boolean>>>({})
    const open = ids.filter((id) => chosen[id] ?? !isBelowMd)

    return (
        <Accordion
            type="multiple"
            value={open}
            onValueChange={(next) =>
                setChosen(Object.fromEntries(ids.map((id) => [id, next.includes(id)])))
            }
            data-hydrated={isHydrated || undefined}
            className={cn(
                'max-md:[&:not([data-hydrated])_[data-slot=accordion-content]]:hidden',
                'max-md:[&:not([data-hydrated])_[data-slot=accordion-trigger]>svg]:rotate-0',
                className,
            )}
        >
            {children}
        </Accordion>
    )
}
